import { useI18n } from '@/lib/i18n'
import {
  type LanguageCode,
  getPathLanguagePrefix,
  isValidLanguageCode,
  stripLanguagePrefix,
  withLanguagePath,
} from '@/lib/url-language-utils'
import { useLayoutEffect } from 'react'

type ArenaSeoPage = 'home' | 'launcher' | 'rules' | 'roles'

const ARENA_ALTERNATES: ReadonlyArray<[LanguageCode, string]> = [
  ['en', 'en'],
  ['zh', 'zh-Hans'],
  ['tw', 'zh-Hant'],
  ['ja', 'ja'],
  ['ko', 'ko'],
  ['ar', 'ar'],
  ['es', 'es'],
  ['fr', 'fr'],
]

const PAGE_PATH: Record<ArenaSeoPage, string> = {
  home: '/',
  launcher: '/new',
  rules: '/play/rule',
  roles: '/play/roles',
}

function setMeta(
  attribute: 'name' | 'property',
  key: string,
  value: string,
): () => void {
  let element = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  const previous = element?.getAttribute('content') ?? null
  const wasPresent = Boolean(element)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.append(element)
  }
  element.content = value
  return () => {
    if (wasPresent) {
      if (previous === null)
        element?.removeAttribute('content')
      else element?.setAttribute('content', previous)
    }
    else {
      element?.remove()
    }
  }
}

export function useArenaSeoHead(page: ArenaSeoPage, contentReady = true) {
  const { language, t } = useI18n()

  useLayoutEffect(() => {
    const pathOnMount = window.location.pathname
    const preludeId = page === 'home' || page === 'launcher'
      ? 'seo-arena-prelude'
      : 'seo-arena-guide-prelude'
    const serverPrelude = document.getElementById(preludeId)
    const currentLanguage = isValidLanguageCode(language) ? language : 'en'
    const htmlLanguage = ARENA_ALTERNATES.find(([code]) => code === currentLanguage)?.[1] ?? 'en'
    const canonicalUrl = `https://werewolf.mentiss.ai${withLanguagePath(PAGE_PATH[page], currentLanguage)}`
    const title = t(page === 'home' || page === 'launcher' ? 'gameSeo.title' : `gameSeo.${page}PageTitle`)
    const description = t(page === 'home' || page === 'launcher' ? 'gameSeo.description' : `gameSeo.${page}PageDescription`)
    const robots = window.location.hostname === 'werewolf.mentiss.ai'
      ? page === 'launcher' ? 'noindex, follow' : 'index, follow'
      : 'noindex, nofollow'
    const previousTitle = document.title
    const previousLang = document.documentElement.lang
    const previousDir = document.documentElement.dir

    document.title = title
    document.documentElement.lang = htmlLanguage
    document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr'
    const restoreMeta = [
      setMeta('name', 'description', description),
      setMeta('name', 'robots', robots),
      setMeta('property', 'og:title', title),
      setMeta('property', 'og:description', description),
      setMeta('property', 'og:url', canonicalUrl),
      setMeta('name', 'twitter:title', title),
      setMeta('name', 'twitter:description', description),
      setMeta('name', 'twitter:url', canonicalUrl),
    ]

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    const previousCanonical = canonical?.getAttribute('href') ?? null
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.rel = 'canonical'
      document.head.append(canonical)
    }
    canonical.href = canonicalUrl

    const previousAlternates = Array.from(
      document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'),
    )
    previousAlternates.forEach(link => link.remove())
    const alternates: HTMLLinkElement[] = []
    for (const [code, hreflang] of ARENA_ALTERNATES) {
      const alternate = document.createElement('link')
      alternate.rel = 'alternate'
      alternate.hreflang = hreflang
      alternate.href = `https://werewolf.mentiss.ai${withLanguagePath(PAGE_PATH[page], code)}`
      document.head.append(alternate)
      alternates.push(alternate)
    }
    const defaultAlternate = document.createElement('link')
    defaultAlternate.rel = 'alternate'
    defaultAlternate.hreflang = 'x-default'
    defaultAlternate.href = `https://werewolf.mentiss.ai${withLanguagePath(PAGE_PATH[page], 'en')}`
    document.head.append(defaultAlternate)
    alternates.push(defaultAlternate)

    const defaultStructuredData = document.head.querySelector('#site-default-structured-data')
    const defaultStructuredDataNext = defaultStructuredData?.nextSibling ?? null
    defaultStructuredData?.remove()
    let structuredData = document.head.querySelector<HTMLScriptElement>('#arena-structured-data')
    const previousStructuredData = structuredData?.textContent ?? null
    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'arena-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.append(structuredData)
    }
    const pageData = {
      name: title,
      description,
      url: canonicalUrl,
      inLanguage: htmlLanguage,
    }
    structuredData.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      ...pageData,
    })

    if (contentReady)
      serverPrelude?.remove()

    return () => {
      restoreMeta.forEach(restore => restore())
      alternates.forEach(link => link.remove())
      previousAlternates.forEach(link => document.head.append(link))
      if (previousCanonical === null)
        canonical.remove()
      else
        canonical.href = previousCanonical
      if (previousStructuredData === null)
        structuredData.remove()
      else
        structuredData.textContent = previousStructuredData
      if (defaultStructuredData) {
        const next = defaultStructuredDataNext?.parentNode === document.head
          ? defaultStructuredDataNext
          : null
        document.head.insertBefore(defaultStructuredData, next)
      }
      document.title = previousTitle
      document.documentElement.lang = previousLang
      document.documentElement.dir = previousDir

      const destinationPath = stripLanguagePrefix(window.location.pathname)
      if (window.location.pathname !== pathOnMount
        && !Object.values(PAGE_PATH).includes(destinationPath)) {
        const destination = new URL(window.location.href)
        if (!getPathLanguagePrefix(destination.pathname))
          destination.pathname = withLanguagePath(destination.pathname, currentLanguage)
        destination.searchParams.delete('lang')
        window.location.replace(destination.href)
      }
    }
  }, [page, language, t, contentReady])
}
